import type { Comment } from '@/@types/comment'
import { PostType } from '@/@types/post'
import {
	DELETE_COMMENT,
	LIKE_COMMENT,
	PIN_COMMENT,
	UNLIKE_COMMENT,
	UNPIN_COMMENT,
} from '@/constants/api'
import usePost from '@/utils/hooks/usePost'
import useUniversalNavigate from '@/utils/hooks/useUniversalNavigate'
import queryClient from '@/utils/queryClient'
import { post } from '@/utils/request'
import { POST_TYPE_TO_ROUTE_MAPPING } from '@/utils/route'
import { type QueryKey, useMutation } from '@tanstack/react-query'
import { cloneDeep, isNil, set, update } from 'lodash-es'
import { MouseEvent, useCallback } from 'react'

export function useCommentLikeToggle(queryKey: QueryKey) {
	const { mutateAsync: toggleLike } = useMutation({
		mutationFn: async (vars) => {
			const comment = queryClient.getQueryData<Comment>(queryKey)
			if (isNil(comment)) {
				return Promise.reject()
			}

			return post(comment.liked ? LIKE_COMMENT : UNLIKE_COMMENT, {
				id: comment.id,
				targetType: comment.targetType,
			}).then((res) => res.data)
		},
		onMutate: async () => {
			// Cancel any outgoing refetches
			// (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries({ queryKey })

			// Snapshot the previous value
			const previousComment = queryClient.getQueryData<Comment>(queryKey)

			// Optimistically update to the new value
			queryClient.setQueryData(queryKey, (prev: Comment | undefined) => {
				if (isNil(prev)) {
					return prev
				}
				const next = cloneDeep(prev)
				if (prev.liked) {
					update(next, 'likeCount', (likeCount: number) => likeCount - 1)
					set(next, 'liked', false)
				} else {
					update(next, 'likeCount', (likeCount: number) => likeCount + 1)
					set(next, 'liked', true)
				}
				return next
			})

			// Return a context object with the snapshotted value
			return { prevData: previousComment }
		},
		// If the mutation fails,
		// use the context returned from onMutate to roll back
		onError: (err, _vars, context) => {
			queryClient.setQueryData(queryKey, context?.prevData)
		},
		// Always refetch after error or success:
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey })
		},
	})
	return {
		toggleLike,
	}
}

export function useCommentPinToggle(queryKey: QueryKey) {
	const { mutateAsync: togglePin } = useMutation({
		mutationFn: async () => {
			const comment = queryClient.getQueryData<Comment>(queryKey)
			if (isNil(comment)) {
				return Promise.reject()
			}
			return post(comment.isPinned ? PIN_COMMENT : UNPIN_COMMENT, {
				id: comment.id,
				targetType: comment.targetType,
			}).then((res) => res.data)
		},
		onMutate: async () => {
			// Cancel any outgoing refetches
			// (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries({ queryKey })

			// Snapshot the previous value
			const previousComment = queryClient.getQueryData<Comment>(queryKey)

			// Optimistically update to the new value
			queryClient.setQueryData(queryKey, (prev: Comment | undefined) => {
				if (isNil(prev)) {
					return prev
				}
				const next = cloneDeep(prev)
				update(next, 'isPinned', (isPinned) => !isPinned)
				return next
			})

			// Return a context object with the snapshotted value
			return { prevData: previousComment }
		},
		// If the mutation fails,
		// use the context returned from onMutate to roll back
		onError: (err, _vars, context) => {
			queryClient.setQueryData(queryKey, context?.prevData)
		},
		// Always refetch after error or success:
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey })
		},
	})
	return {
		togglePin,
	}
}

export function useCommentDelete(queryKey: QueryKey) {
	const { mutateAsync: deleteComment } = useMutation({
		mutationFn: async () => {
			const comment = queryClient.getQueryData<Comment>(queryKey)
			if (isNil(comment)) {
				return Promise.reject()
			}
			return post(DELETE_COMMENT, {
				id: comment.id,
				targetType: comment.targetType,
			}).then((res) => res.data)
		},
		onMutate: async () => {
			// Cancel any outgoing refetches
			// (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries({ queryKey })

			// Snapshot the previous value
			const previousComment = queryClient.getQueryData<Comment>(queryKey)

			// Optimistically update to the new value
			queryClient.setQueryData(queryKey, (prev: Comment | undefined) => {
				if (isNil(prev)) {
					return prev
				}
				const next = cloneDeep(prev)
				set(next, 'deleted', true)
				return next
			})

			// Return a context object with the snapshotted value
			return { prevData: previousComment }
		},
		// If the mutation fails,
		// use the context returned from onMutate to roll back
		onError: (err, _vars, context) => {
			queryClient.setQueryData(queryKey, context?.prevData)
		},
		// Always refetch after error or success:
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey })
		},
	})
	return {
		deleteComment,
	}
}

export function useToCommentPage({ targetId, level, targetType, id }: Comment) {
	const postId = targetId
	const targetPost = usePost(targetId)
	const targetPostUserName = targetPost?.user?.username
	const navigate = useUniversalNavigate()

	const canToCommentPage = !!(targetPostUserName && postId && level <= 1)
	const toCommentPage = useCallback(
		(e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => {
			const type = targetType && POST_TYPE_TO_ROUTE_MAPPING[targetType as PostType]

			if (!canToCommentPage || window.getSelection()?.toString().length || !type) {
				return
			}
			e.stopPropagation()

			if (level <= 1) {
				navigate(
					{
						to: '/u/$username/$type/$id/$commentId',
						params: {
							username: targetPostUserName,
							id: postId,
							type,
							commentId: id,
						},
					},
					e
				)
			}
		},
		[canToCommentPage, id, level, postId, targetType, targetPostUserName, navigate]
	)

	return {
		canToCommentPage,
		toCommentPage,
	}
}
